/**
 * The picture that stands for a thing.
 *
 * A recipe has had one since it could hold pictures: the first picture anybody
 * added goes on its card and across the top of its page, until whoever keeps the
 * recipe opens another and presses *Make this the main picture*. Jay: *"Can we
 * have the same behavior for stories and anything else that could use a picture
 * for its main node."* So this is that rule, for everything the family can add
 * pictures to — a simcha or an event, a story, the day somebody was born, a
 * recipe — said once:
 *
 *   - **the first picture added is the main one**, so a thing wears a face the
 *     moment it has a picture and nobody has to set anything. (A simcha made
 *     with a picture of its own starts with that one: it *is* the first.)
 *   - **somebody who may change the thing can choose another**, by opening it
 *     large — which is when anybody knows whether it is the one;
 *   - **a choice that can no longer be shown falls back** rather than leaving a
 *     hole: a picture an administrator took down quietly stops being the main
 *     one, and the first one still standing takes over.
 *
 * Where the choice is kept differs, and only here knows it: a recipe names its
 * picture (`Recipe.coverPhotoId`, as it always has), while for a day, a story or
 * a birth it is a mark on the picture itself (`FamilyPhoto.isCover`) — a birth
 * has no record of its own to write a name on, and one mark on a picture serves
 * all three and whatever comes next. Nothing else in the app should care.
 *
 * Stills only, like a recipe's: the main picture is drawn in circles and squares
 * a few dozen pixels across, where a clip or a voice note can be neither.
 */
import { byId } from '../lib/grouped';
import { addedTo, isOwnDay } from './milestones';
import { managesPerson, type Actor } from './permissions';
import { recipeCover, recipeEditRight, recipePictures } from './recipes';
import { storyEditRight } from './storyEditing';
import type { FamilyPhoto, MemoryAnchor, Recipe, Story } from './types';

const NO_RECIPES: readonly Recipe[] = [];

const isStill = (m: FamilyPhoto) => !!m.storageKey && !m.removed && !!m.mimeType?.startsWith('image/');

/**
 * The still pictures a thing holds, in the order they arrived — which is the
 * order that decides the main one, so "first" has to mean the same thing
 * everywhere. A simcha's own picture comes before anything added to it.
 */
export function anchorPictures(moments: FamilyPhoto[], anchor: MemoryAnchor): FamilyPhoto[] {
  if (anchor.kind === 'recipe') return recipePictures(moments, anchor.id);
  const added = addedTo(moments, anchor)
    .filter(isStill)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
  const own = anchor.kind === 'moment' ? byId(moments).get(anchor.id) : undefined;
  return own && isStill(own) ? [own, ...added] : added;
}

/** The main picture of a day, a story, a birth or a recipe — or nothing, when it has no picture yet. */
export function coverOf(anchor: MemoryAnchor, moments: FamilyPhoto[], recipes: readonly Recipe[] = NO_RECIPES): FamilyPhoto | undefined {
  if (anchor.kind === 'recipe') {
    const recipe = byId(recipes).get(anchor.id);
    return recipe ? recipeCover(recipe, moments) : undefined;
  }
  const pictures = anchorPictures(moments, anchor);
  return pictures.find((p) => p.isCover) ?? pictures[0];
}

/**
 * What an opened picture could be the main picture *of*: whatever it was added
 * to — or itself, when it is a simcha's own picture (choosing that one is how
 * the family goes back to it after trying another).
 */
export function coverAnchorOf(photo: FamilyPhoto): MemoryAnchor | null {
  if (photo.addedTo) return photo.addedTo;
  return isOwnDay(photo) && !!photo.storageKey ? { kind: 'moment', id: photo.id } : null;
}

/**
 * Whether this viewer may choose the main picture, and why.
 *
 *   'own'   — it is theirs to look after: they recorded the story, they put the
 *             day on the family's line, it is their circle (or one they help
 *             with), they keep the recipe — or they are a Family Editor, who may
 *             tidy any day or circle already.
 *   'admin' — only because Admin mode is on; drawn in the admin colour, like
 *             every other power ordinary members do not have.
 *   'none'  — everybody else. Anyone may *add* a picture; which one stands for
 *             the thing is its keeper's to say.
 *
 * It follows the rule each thing already has for being changed at all
 * (`storyEditRight`, `recipeEditRight`, a Moment's uploader-or-editor, a
 * circle's `managesPerson`) rather than inventing a fifth.
 */
export type CoverRight = 'own' | 'admin' | 'none';

export function coverRight(
  actor: Actor,
  anchor: MemoryAnchor,
  things: { moments: FamilyPhoto[]; stories: readonly Pick<Story, 'id' | 'creatorAccountId' | 'status'>[]; recipes: readonly Recipe[] },
): CoverRight {
  const role = actor.session.role;
  const byRole: CoverRight = role === 'admin' ? 'admin' : role === 'editor' ? 'own' : 'none';
  if (anchor.kind === 'recipe') {
    const recipe = byId(things.recipes).get(anchor.id);
    return !recipe || recipe.status === 'archived' ? 'none' : recipeEditRight(actor, recipe);
  }
  if (anchor.kind === 'story') {
    const story = byId(things.stories).get(anchor.id);
    if (!story || story.status === 'archived') return 'none';
    const right = storyEditRight(actor, story);
    return right === 'suggest' ? 'none' : right;
  }
  if (anchor.kind === 'moment') {
    const day = byId(things.moments).get(anchor.id);
    if (!day || day.removed) return 'none';
    return day.uploadedByAccountId === actor.session.accountId ? 'own' : byRole;
  }
  return managesPerson(actor, anchor.id) ? 'own' : byRole;
}
